/**
 * Boot intro (issue #86): a one-time overhead-hold -> eased-sweep -> settled
 * low-orbit pose, so a first-time visitor sees the house framed against the
 * turbine array instead of a flat top-down view. Orbit mode only — Fly,
 * Walk, and Route are unaffected (out of scope) and inherit whatever pose
 * this sequence leaves the camera at, exactly as any other mode switch does
 * today.
 *
 * Deliberately three.js-free, engine-agnostic pure module (mirrors
 * groundLock.ts / viewpointJump.ts) so the pose maths and the
 * hold/sweep/cancel state machine are unit-tested in Node; main.ts owns the
 * DOM/three.js glue (input listeners, applying `pose` to the live camera,
 * reading the collider for `groundAvailable`).
 *
 * Hard constraint #7 (a missing input is a reported blocker, never fabricated
 * data): the anchor is read from data/config/viewpoints.json's first entry
 * via {@link resolveBootIntroAnchor}, never hardcoded to the local origin —
 * today's default viewpoint happens to sit exactly at the local origin, but
 * this module never assumes that. A missing file or an unresolved coordinate
 * there means no intro at all (the caller reports the reason and boots the
 * existing static pose). Hard constraint #6 (elevations only ever come from
 * the DTM-derived collider): the authored end-pose elevation is a *target*
 * for the sweep, not an assertion — the caller's existing camera terrain
 * floor (issue #50) is free to lift it, and this module never seats the
 * orbit pivot's vertical position itself (that remains cameraRig.ts's
 * seatOrbitTargetIfNeeded, consumed here, not modified).
 */
import { bngToLocal, type LocalXZ } from '../core/coordinates';
import type { ViewpointsFile } from '../core/manifest';

export interface IntroPose {
  x: number;
  /** metres AOD */
  y: number;
  z: number;
}

export interface BootIntroAnchorResult {
  anchor: LocalXZ | null;
  /** Populated only when `anchor` is null — the hard-constraint-#7 reason to log, never to guess from. */
  reason?: string;
}

/**
 * Resolves the boot intro's anchor from the viewpoints config's first entry.
 * Never hardcodes a coordinate: a missing file, an empty list, or an
 * unresolved (null) coordinate all return `anchor: null` with a reason
 * string for the caller to report (hard constraint #7) rather than guess.
 */
export function resolveBootIntroAnchor(viewpointsFile: ViewpointsFile | undefined): BootIntroAnchorResult {
  const first = viewpointsFile?.viewpoints[0];
  if (!first) {
    return { anchor: null, reason: 'no viewpoints are configured to anchor the boot intro' };
  }
  if (first.easting === null || first.northing === null) {
    return {
      anchor: null,
      reason: `default viewpoint "${first.id}" has no resolved coordinates yet (coordinate_status: ${first.coordinate_status})`,
    };
  }
  return { anchor: bngToLocal(first.easting, first.northing) };
}

/**
 * Storage-like interface for the once-per-session play flag, deliberately
 * narrow (get/set only) and injected rather than importing
 * `window.sessionStorage` directly — same shape/contract as
 * `ui/hudPrefs.ts`'s `HudPrefsStorage` and `core/quality.ts`'s
 * `QualityStorage`. Both methods may throw (private browsing); callers of
 * {@link shouldPlayBootIntro} never need to catch — it absorbs that itself.
 */
export interface IntroSessionStorage {
  get(key: string): string | null | undefined;
  set(key: string, value: string): void;
}

export const BOOT_INTRO_SESSION_KEY = 'ray2.bootIntroPlayed';

/**
 * Decides whether the boot intro should play on this load.
 * `prefers-reduced-motion: reduce` always wins and skips it outright,
 * regardless of storage. Otherwise the intro plays once per browser session:
 * a reload (including issue #73's quality-switch reload) within the same
 * session must not replay it. An unavailable or throwing storage is treated
 * as a harmless default — play, never crash — since skipping a cosmetic
 * boot flourish is not the kind of fabricated-data risk hard constraint #7
 * guards against.
 */
export function shouldPlayBootIntro(storage: IntroSessionStorage | null, prefersReducedMotion: boolean): boolean {
  if (prefersReducedMotion) return false;
  if (!storage) return true;
  try {
    if (storage.get(BOOT_INTRO_SESSION_KEY)) return false;
    storage.set(BOOT_INTRO_SESSION_KEY, '1');
    return true;
  } catch {
    return true;
  }
}

/** Horizontal offset (metres, local x/z) of the authored end pose from the anchor. */
export interface IntroEndOffsetM {
  dx: number;
  dz: number;
}

/** Boot height above the anchor for the top-down start pose, metres — matches main.ts's existing default boot pose. */
export const DEFAULT_BOOT_HEIGHT_M = 250;
/** Hold duration at the top-down start pose, seconds (also lets nearby tiles stream in). */
export const DEFAULT_HOLD_DURATION_S = 2;
/** Eased sweep duration, seconds. */
export const DEFAULT_SWEEP_DURATION_S = 5;
/** Extra time, past the base hold, to wait for terrain at the anchor before sweeping anyway. */
export const DEFAULT_TERRAIN_WAIT_CAP_S = 10;
/**
 * Authored end-pose offset from the anchor, local x/z metres — derived from
 * the reference screenshot's readout (E 390987.6, N 586279.7 against the
 * anchor E 390978, N 586358 == local origin): ~79 m south, slightly east.
 * Relative to the anchor (not the scene origin) so it tracks a moved anchor.
 */
export const DEFAULT_END_OFFSET_M: IntroEndOffsetM = { dx: 9.6, dz: 78.3 };
/** Authored end-pose target elevation, metres AOD — subject to the existing camera terrain floor (issue #50). */
export const DEFAULT_END_Y_AOD = 183.2;

/** Camera directly above the anchor at boot height, looking straight down. */
export function introStartPose(anchor: LocalXZ, bootHeightM: number): IntroPose {
  return { x: anchor.x, y: bootHeightM, z: anchor.z };
}

/** Authored low end pose: anchor + the fixed south/east offset, at the authored target elevation. */
export function introEndPose(anchor: LocalXZ, endOffsetM: IntroEndOffsetM, endYAod: number): IntroPose {
  return { x: anchor.x + endOffsetM.dx, y: endYAod, z: anchor.z + endOffsetM.dz };
}

/**
 * Standard ease-in-out cubic, t in [0,1] -> [0,1]. Slower than linear near
 * both ends (the "hold, then ease into motion, then ease into rest" feel),
 * exactly linear at the midpoint.
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function clamp01(t: number): number {
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

export type IntroPhase = 'hold' | 'sweep' | 'settled';

export interface BootIntroConfig {
  anchor: LocalXZ;
  bootHeightM?: number;
  endOffsetM?: IntroEndOffsetM;
  endYAod?: number;
  holdDurationS?: number;
  sweepDurationS?: number;
  terrainWaitCapS?: number;
}

export interface IntroTickContext {
  /** Whether the collider currently reports ground at the anchor. Only consulted once the base hold has elapsed. */
  groundAvailable: boolean;
  /** Any qualifying user input this frame (pointer down/drag, wheel, nav key, mode switch, viewpoint select). */
  cancelRequested: boolean;
}

/**
 * Pure hold -> sweep -> settled state machine driving the boot intro's
 * camera pose. `pose` is a shared, mutated-in-place scratch object (mirrors
 * cameraRig.ts's `floorScratch` pattern) so ticking the intro every frame for
 * its few-second lifetime allocates nothing beyond the one-off construction —
 * callers must read `pose` immediately after `tick()` returns `true` and
 * never retain the reference across frames.
 */
export class BootIntroSweep {
  readonly startPose: IntroPose;
  readonly endPose: IntroPose;
  /** Current camera pose for this tick; mutated in place, read-and-discard. */
  readonly pose: IntroPose;
  phase: IntroPhase = 'hold';

  private readonly holdDurationS: number;
  private readonly sweepDurationS: number;
  private readonly terrainWaitCapS: number;
  private holdElapsedS = 0;
  private sweepElapsedS = 0;

  constructor(config: BootIntroConfig) {
    const bootHeightM = config.bootHeightM ?? DEFAULT_BOOT_HEIGHT_M;
    const endOffsetM = config.endOffsetM ?? DEFAULT_END_OFFSET_M;
    const endYAod = config.endYAod ?? DEFAULT_END_Y_AOD;
    this.holdDurationS = config.holdDurationS ?? DEFAULT_HOLD_DURATION_S;
    this.sweepDurationS = config.sweepDurationS ?? DEFAULT_SWEEP_DURATION_S;
    this.terrainWaitCapS = config.terrainWaitCapS ?? DEFAULT_TERRAIN_WAIT_CAP_S;

    this.startPose = introStartPose(config.anchor, bootHeightM);
    this.endPose = introEndPose(config.anchor, endOffsetM, endYAod);
    this.pose = { ...this.startPose };
  }

  /** True while the intro still owns the camera pose (not settled/cancelled). */
  get active(): boolean {
    return this.phase !== 'settled';
  }

  /**
   * Advances the intro by `dt` seconds. Returns `true` when `pose` was
   * (re)computed and should be applied to the camera this frame, `false`
   * once the intro has handed control back — a cancellation hands back at
   * whatever pose was last applied ("never fight the user"); reaching the
   * sweep's end hands back exactly at `endPose`. Once settled, every further
   * call is a no-op returning `false` without touching `pose`.
   */
  tick(dt: number, ctx: IntroTickContext): boolean {
    if (this.phase === 'settled') return false;
    if (ctx.cancelRequested) {
      this.phase = 'settled';
      return false;
    }

    if (this.phase === 'hold') {
      this.holdElapsedS += dt;
      const waitElapsedS = Math.max(0, this.holdElapsedS - this.holdDurationS);
      const pastBaseHold = this.holdElapsedS >= this.holdDurationS;
      const terrainReady = ctx.groundAvailable || waitElapsedS >= this.terrainWaitCapS;
      if (pastBaseHold && terrainReady) {
        this.phase = 'sweep';
        this.sweepElapsedS = 0;
      }
      // Sweep hasn't advanced yet even on the transitioning frame — the very
      // next tick begins interpolating from t=0. One extra held frame
      // (<= one frame time) is imperceptible and keeps this branch simple.
      this.pose.x = this.startPose.x;
      this.pose.y = this.startPose.y;
      this.pose.z = this.startPose.z;
      return true;
    }

    // phase === 'sweep'
    this.sweepElapsedS += dt;
    const t = clamp01(this.sweepElapsedS / this.sweepDurationS);
    const eased = easeInOutCubic(t);
    this.pose.x = this.startPose.x + (this.endPose.x - this.startPose.x) * eased;
    this.pose.y = this.startPose.y + (this.endPose.y - this.startPose.y) * eased;
    this.pose.z = this.startPose.z + (this.endPose.z - this.startPose.z) * eased;
    if (t >= 1) {
      this.pose.x = this.endPose.x;
      this.pose.y = this.endPose.y;
      this.pose.z = this.endPose.z;
      this.phase = 'settled';
    }
    return true;
  }
}
